import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { LoginPage } from "./pages/login/login-page";
import { CallbackPage } from "./pages/callback/callback-page";
import { RootLayout } from "./components/root-layout";
import { ProtectedRoutes } from "./components/protected-routes";
import { DashboardPage } from "./pages/dashboard/dashboard-page";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider as MuiThemeProvider, CssBaseline } from "@mui/material";
import { TaggingPage } from "./pages/tagging/tagging-page";
import { ThemeStructureGuard } from "./pages/tagging/theme-structure-guard";
import { ThemesPage } from "./pages/themes/themes-page";
import { OutletWithMenu } from "./components/outlet-with-menu";
import { GuidelinesPage } from "./pages/guidelines/guidelines-page";
import { DonePage } from "./pages/done/done-page";
import { appUrls } from "./utils/use-get-page-url";
import { muiTheme } from "./mui-theme";
import { ThemeProvider } from "./components/theme-provider";
import { Toaster } from "./components/ui/toaster";
import { useVersionNotifier } from "./utils/use-version-notifier";
import { ErrorBoundary } from "./components/error-boundary";
import { useEffect } from "react";
import { initAnalytics, trackPage } from "./utils/analytics";

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: appUrls.home,
        element: <ProtectedRoutes />,
        children: [
          {
            path: "/done",
            element: <DonePage />,
          },
          {
            path: `${appUrls.themes}/:organizationId`,
            element: <ThemesPage />,
          },
          {
            path: `${appUrls.organization}/:organizationId${appUrls.guidelines}`,
            element: <GuidelinesPage />,
          },
          {
            path: appUrls.home,
            element: <OutletWithMenu />,
            children: [
              {
                path: appUrls.home,
                element: <DashboardPage />,
              },
              {
                path: `${appUrls.organization}/:organizationId${appUrls.responses}/:responseId?`,
                element: (
                  <ThemeStructureGuard>
                    <TaggingPage />
                  </ThemeStructureGuard>
                ),
              },
            ],
          },
        ],
      },
      {
        path: appUrls.login,
        element: <LoginPage />,
      },
      {
        path: appUrls.callback,
        element: <CallbackPage />,
      },
      {
        path: "*",
        element: <div>Not found</div>,
      },
    ],
  },
]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

function AppContent() {
  useVersionNotifier();

  // Initialize analytics and subscribe to router events
  useEffect(() => {
    // Initialize analytics
    initAnalytics();

    // Subscribe to router navigation events for page tracking
    const unsubscribe = router.subscribe((state) => {
      const page = state.location.pathname;
      const title = document.title;
      trackPage(page, title);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <>
      <RouterProvider router={router} />
      <Toaster />
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <ThemeProvider defaultTheme="system" storageKey="classificator-theme">
          <MuiThemeProvider theme={muiTheme}>
            <CssBaseline />
            <AppContent />
          </MuiThemeProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
